import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, AlertTriangle, CheckCircle, Clock } from "lucide-react";

const MODEL_STYLES = {
  uniform: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Uniform" },
  corporate_varies: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "Corp. Varies" },
  franchise_varies: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", label: "Franchise Varies" },
  unknown: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-500", label: "Unknown" },
};

export default function ChainConsistencyPanel() {
  const [chains, setChains] = useState([]);
  const [propagations, setPropagations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API}/admin/chain-consistency/all`),
        axios.get(`${API}/admin/chain-consistency/propagations?limit=10`),
      ]);
      setChains(cRes.data.chains || []);
      setPropagations(pRes.data.propagations || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const updateModel = async (chainName) => {
    setUpdating(chainName);
    try {
      const res = await axios.post(`${API}/admin/chain-consistency/update-model/${encodeURIComponent(chainName)}`);
      toast.success(`${chainName}: ${res.data.participation_model} (${res.data.consistency_score})`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally { setUpdating(null); }
  };

  const updateAll = async () => {
    setUpdating("__all__");
    try {
      const res = await axios.post(`${API}/admin/chain-consistency/update-all`);
      toast.success(`Updated ${res.data.chains_updated} chains`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Batch update failed");
    } finally { setUpdating(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const lowConsistency = chains.filter(c => (c.consistency_score || 0) < 0.6 && c.consistency_score !== null);

  return (
    <div className="space-y-4" data-testid="chain-consistency-panel">
      {/* Summary */}
      <div className="grid grid-cols-4 gap-2">
        {["uniform", "corporate_varies", "franchise_varies", "unknown"].map(model => {
          const s = MODEL_STYLES[model];
          const count = chains.filter(c => (c.participation_model || "unknown") === model).length;
          return (
            <div key={model} className={`${s.bg} border ${s.border} rounded-lg p-2 text-center`}>
              <p className={`text-lg font-bold ${s.text}`} data-testid={`model-${model}-count`}>{count}</p>
              <p className="text-[9px] text-slate-500">{s.label}</p>
            </div>
          );
        })}
      </div>

      {/* Update all button */}
      <button onClick={updateAll} disabled={updating === "__all__"}
        className="w-full flex items-center justify-center gap-1.5 py-1.5 bg-slate-100 text-slate-600 text-[11px] font-semibold rounded-lg hover:bg-slate-200 transition"
        data-testid="update-all-btn">
        {updating === "__all__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Recalculate All Chain Models
      </button>

      {/* Low consistency warning */}
      {lowConsistency.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="low-consistency-warning">
          <p className="text-[11px] font-semibold text-red-700 flex items-center gap-1 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5" /> Low Consistency ({lowConsistency.length})
          </p>
          <div className="space-y-1">
            {lowConsistency.slice(0, 5).map(c => (
              <div key={c.chain_name} className="flex items-center gap-2 text-xs text-red-600">
                <span className="font-medium">{c.chain_name}</span>
                <span className="text-[10px]">{(c.consistency_score * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent propagations */}
      {propagations.length > 0 && (
        <div data-testid="recent-propagations">
          <p className="text-[11px] font-semibold text-slate-600 mb-1.5 flex items-center gap-1">
            <Link2 className="w-3.5 h-3.5" /> Recent Propagations
          </p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {propagations.map((p, i) => (
              <div key={i} className="flex items-center gap-2 p-2 bg-sky-50 border border-sky-200 rounded-lg text-xs"
                   data-testid={`propagation-${i}`}>
                <span className="font-medium text-sky-700">{p.chain_name}</span>
                <span className="text-slate-400">{p.field_changed}</span>
                <span className="text-slate-400">from {p.source_city}</span>
                <span className="ml-auto text-[10px] text-sky-600 font-semibold">{p.affected_locations} locations</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chain list */}
      {chains.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4">No multi-location chains found.</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="chain-list">
          {chains.slice(0, 30).map((c) => {
            const model = c.participation_model || "unknown";
            const ms = MODEL_STYLES[model] || MODEL_STYLES.unknown;
            const score = c.consistency_score;
            return (
              <div key={c.chain_name}
                className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-lg"
                data-testid={`chain-${c.chain_name}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-800 truncate">{c.chain_name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${ms.bg} ${ms.text}`}>{ms.label}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                    <span>{c.location_count} locations</span>
                    {score !== null && score !== undefined && <span>{(score * 100).toFixed(0)}% consistent</span>}
                    {c.cities?.length > 0 && <span className="truncate">{c.cities.slice(0, 3).join(", ")}</span>}
                  </div>
                </div>
                <button onClick={() => updateModel(c.chain_name)} disabled={updating === c.chain_name}
                  className="px-2 py-1 bg-slate-50 text-slate-500 rounded text-[10px] hover:bg-slate-100 transition flex-shrink-0"
                  data-testid={`update-btn-${c.chain_name}`}>
                  {updating === c.chain_name ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
